import { Add, Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from '@mui/material';
import { Fragment, useCallback, useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PercentageField } from '~/components/GenericForm/PercentageField';
import { CostCenter } from '~/data/costCenters';
import { CustomDataDialogComponent } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/CustomDataDialogComponent';
import {
  UseAdditionalCostCentersProps,
  useCostCentersList,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/useCostCentersList';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/useStyles';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

type AddAdditionalCostCenterTableRowProps = {
  costCenterOptions: CostCenter[];
  disabledCostCenterOptions: CostCenter[];
  hasAdditionalCostCenters: boolean;
  onAddCostCenterRow: (selectedCostCenter: CostCenter) => void;
};

const AddAdditionalCostCenterTableRow = (
  props: AddAdditionalCostCenterTableRowProps,
) => {
  const {
    costCenterOptions,
    disabledCostCenterOptions,
    hasAdditionalCostCenters,
    onAddCostCenterRow,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const isCostCenterOptionDisabled = useCallback(
    (costCenterId: number) => {
      return disabledCostCenterOptions.some(
        (cc) => cc.fCostCenterId === costCenterId,
      );
    },
    [disabledCostCenterOptions],
  );

  const [selectedCostCenter, setSelectedCostCenter] = useState<
    CostCenter | undefined
  >(undefined);

  return (
    <>
      {costCenterOptions.length > disabledCostCenterOptions.length && (
        <TableRow>
          <TableCell colSpan={hasAdditionalCostCenters ? 4 : 3}>
            <Box sx={sxStyles.addCostCenter}>
              <Autocomplete
                options={costCenterOptions}
                getOptionLabel={(c) => {
                  return c.fCostCenterId !== -1
                    ? `${c.fCostCenterCode}, ${c.fCostCenterName}`
                    : '';
                }}
                getOptionDisabled={(c) =>
                  isCostCenterOptionDisabled(c.fCostCenterId)
                }
                onChange={(e, newValue) => {
                  setSelectedCostCenter(!!newValue ? newValue : undefined);
                }}
                value={selectedCostCenter ?? null}
                size={'small'}
                data-testid="additional-cost-center-autocomplete"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('fEmpAdditionalCostCenters')}
                    data-testid="additional-cost-center-input"
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                )}
                fullWidth={true}
                disableClearable={false}
              />
              <Button
                onClick={() => {
                  if (!!selectedCostCenter) {
                    onAddCostCenterRow(selectedCostCenter);
                    setSelectedCostCenter(undefined);
                  }
                }}
                startIcon={<Add />}
                size={'small'}
                fullWidth={true}
                data-testid="cost-center-add-button"
                disabled={!selectedCostCenter}
              >
                {t('add')}
              </Button>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

type PropsType = UseAdditionalCostCentersProps;

export const CostCentersListComponent = (props: PropsType) => {
  const {
    isLoading,
    control,
    index,
    fieldArray: { fields, remove },
    costCenterOptions,
    disabledCostCenterOptions,
    onAddCostCenterRow,
    getCostCenterName,
    recalculateTotal,
    customFields,
    onListUpdate,
  } = useCostCentersList(props);

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const [selectedCustomDataDialog, setSelectedCustomDataDialog] = useState<
    null | number
  >(null);

  const hasAdditionalCostCenters = fields.length > 1;

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <Box>
      <Table size={'small'}>
        <TableBody>
          {fields.map((field, ccIndex) => {
            const costCenterName = getCostCenterName(field.costCenterId);

            const showSubHeading = hasAdditionalCostCenters && ccIndex === 0;

            return (
              <Fragment key={field.id}>
                <TableRow>
                  <TableCell>
                    {costCenterName}{' '}
                    {field.isDefault && (
                      <Box component={'span'} sx={sxStyles.defaultIndicator}>
                        {`(${t('laDefault')})`}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={sxStyles.percentageCell}>
                    <Controller
                      control={control}
                      name={`schedule.${index}.empPosition.costCenterDetails.${ccIndex}.percent`}
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => {
                        return (
                          <PercentageField
                            value={value ?? ''}
                            onChange={(event) => {
                              onChange(event);
                              recalculateTotal();
                              onListUpdate();
                            }}
                            error={!!error}
                            helperText={error?.message}
                            size={'small'}
                            fullWidth={true}
                            data-testid="cost-center-percentage-input"
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell sx={sxStyles.customDataCell}>
                    <Button
                      size={'small'}
                      onClick={() =>
                        setSelectedCustomDataDialog(field.costCenterId)
                      }
                      sx={sxStyles.customDataButton}
                      data-testid="cost-center-custom-data-button"
                    >
                      {t('laCustomData')}
                    </Button>
                  </TableCell>
                  {hasAdditionalCostCenters && (
                    <TableCell sx={sxStyles.deleteCell}>
                      {!field.isDefault && (
                        <IconButton
                          onClick={() => {
                            remove(ccIndex);
                            recalculateTotal();
                            onListUpdate();
                          }}
                          title={t('delete')}
                          data-testid="cost-center-delete-button"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
                {showSubHeading && (
                  <TableRow>
                    <TableCell
                      sx={sxStyles.subHeadingCell}
                      colSpan={hasAdditionalCostCenters ? 4 : 3}
                    >
                      {t('fEmpAdditionalCostCenters')}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
          <AddAdditionalCostCenterTableRow
            costCenterOptions={costCenterOptions}
            disabledCostCenterOptions={disabledCostCenterOptions}
            hasAdditionalCostCenters={hasAdditionalCostCenters}
            onAddCostCenterRow={onAddCostCenterRow}
          />
          <Controller
            control={control}
            name={`schedule.${index}.empPosition.totalCostCenterPercentage`}
            render={({ field: { value } }) => {
              // Validate percentage, without preventing form submit.
              // Previously applied using rules={{validate: (value) => { ... }}}
              const validate = (value: number): boolean | string => {
                if (hasAdditionalCostCenters) {
                  return value === 100 || t('errorTotalPercentageNot100');
                }

                return value <= 100 || t('errorTotalPercentageExceeds100');
              };
              const getError = (
                validationResult: boolean | string,
              ): FieldError | undefined => {
                if (typeof validationResult === 'string') {
                  return {
                    type: '',
                    message: validationResult,
                  };
                }

                return undefined;
              };

              const error: FieldError | undefined = getError(validate(value));

              return (
                <>
                  <TableRow>
                    <TableCell sx={sxStyles.totalCell}>{t('total')}</TableCell>
                    <TableCell sx={sxStyles.percentageTotalCell}>
                      {`${value} %`}
                    </TableCell>
                    <TableCell></TableCell>
                    {hasAdditionalCostCenters && <TableCell></TableCell>}
                  </TableRow>
                  {!!error && (
                    <TableRow>
                      <TableCell
                        sx={sxStyles.errorCell}
                        colSpan={hasAdditionalCostCenters ? 4 : 3}
                      >
                        {error?.message}
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            }}
          />
        </TableBody>
      </Table>
      {!!selectedCustomDataDialog && (
        <CustomDataDialogComponent
          open
          control={control}
          onClose={() => {
            setSelectedCustomDataDialog(null);
            onListUpdate();
          }}
          customFields={customFields[selectedCustomDataDialog]}
        />
      )}
    </Box>
  );
};
